import React, { useState } from 'react';
import { Box, Typography, Grid, Card, CardContent, Stack, TextField, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Avatar, Button, Chip, IconButton, Dialog, DialogContent, DialogTitle, Tooltip } from '@mui/material';
import { RateReview, PendingActions, CheckCircleOutline, AssignmentInd, Search, Add as AddIcon, Close as CloseIcon, RestartAlt, Visibility, History } from '@mui/icons-material';
import CreateAppraisal from './createAppraisal';
import { useNavigate } from 'react-router-dom';

const StatCard = ({ title, value, icon, color }) => (
    <Card
        sx={{
            borderRadius: '16px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            height: '100%',
            background: color.includes('gradient')
                ? color
                : `linear-gradient(135deg, ${color} 0%, ${color}dd 100%)`,
            color: '#FFFFFF',
            position: 'relative',
            overflow: 'hidden',
            border: 'none'
        }}
    >
        <CardContent sx={{ position: 'relative', zIndex: 1, px: 3, py: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
                <Box>
                    <Typography
                        variant="caption"
                        sx={{
                            textTransform: 'uppercase',
                            letterSpacing: '1px',
                            fontWeight: '600',
                            opacity: 0.9,
                            display: 'block'
                        }}
                    >
                        {title}
                    </Typography>
                    <Typography variant="h6" fontWeight="800" sx={{ mt: 0.5 }}>
                        {value}
                    </Typography>
                </Box>
                <Box
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.2)',
                        p: 1.5,
                        borderRadius: '12px',
                        display: 'flex',
                        backdropFilter: 'blur(4px)'
                    }}
                >
                    {React.cloneElement(icon, { sx: { color: '#FFFFFF', fontSize: 28 } })}
                </Box>
            </Stack>
        </CardContent>
        {/* Decorative Circle */}
        <Box
            sx={{
                position: 'absolute',
                top: '-20%',
                right: '-10%',
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.1)',
                zIndex: 0
            }}
        />
    </Card>
);

const MOCK_APPRAISALS = [
    { id: 1, name: 'Alex Johnson', role: 'Frontend Developer', period: 'Q1 2024', status: 'Submitted for Review', score: 0, avatar: 'A' },
    { id: 2, name: 'Sarah Williams', role: 'UI Designer', period: 'Q1 2024', status: 'Completed', score: 4.8, avatar: 'S' },
    { id: 3, name: 'Michael Chen', role: 'Backend Lead', period: 'Q1 2024', status: 'Pending', score: 0, avatar: 'M' },
    { id: 4, name: 'Emily Lee', role: 'Project Manager', period: 'Q1 2024', status: 'In Progress', score: 0, avatar: 'E' },
    { id: 5, name: 'David Kim', role: 'QA Engineer', period: 'Q1 2024', status: 'Overdue', score: 3, avatar: 'D' },
];

function EmployeeAppraisals() {
    const navigate = useNavigate();
    const [filter, setFilter] = useState('All');
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // Filter Logic
    const filteredData = MOCK_APPRAISALS.filter(item => {
        const matchesFilter = filter === 'All' || item.status === filter;
        const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesFilter && matchesSearch;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return { bg: '#E1FCEF', text: '#14532D' };
            case 'Pending Manager': return { bg: '#FFF7ED', text: '#9A3412' };
            case 'Pending Employee': return { bg: '#EFF6FF', text: '#1E40AF' };
            default: return { bg: '#F3F4F6', text: '#374151' };
        }
    };

    return (
        <Box sx={{ p: 1 }}>
            <Grid container spacing={3} sx={{ mb: 4 }}>
                {[
                    { title: 'Total Appraisals', value: 40, icon: <AssignmentInd />, color: "#6366F1" },
                    { title: 'Pending', value: 5, icon: <RateReview />, color: "#F59E0B" },
                    { title: 'Completed', value: 35, icon: <PendingActions />, color: "#3B82F6" },
                    { title: 'Overdue', value: 12, icon: <CheckCircleOutline />, color: "#EF4444" },
                ].map((card, idx) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={idx}>
                        <StatCard
                            title={card.title}
                            value={card.value || 0}
                            icon={card.icon}
                            color={card.color}
                        />
                    </Grid>
                ))}
            </Grid>

            <Grid container spacing={2} alignItems="center" mb={3}>
                <Grid size={{ xs: 12, md: 5 }}>
                    <TextField
                        fullWidth
                        size="small"
                        placeholder="Search by name..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} /> }}
                    />
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Period"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Q1 2026">Q1 2026</MenuItem>
                        <MenuItem value="Q2 2025">Q2 2025</MenuItem>
                        <MenuItem value="Q1 2025">Q1 2025</MenuItem>
                    </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 2 }}>
                    <TextField
                        select
                        fullWidth
                        size="small"
                        label="Status"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    >
                        <MenuItem value="All">All</MenuItem>
                        <MenuItem value="Pending Manager">Pending Manager</MenuItem>
                        <MenuItem value="Pending Employee">Pending Employee</MenuItem>
                        <MenuItem value="Completed">Completed</MenuItem>
                    </TextField>
                </Grid>
                <Grid size={{ xs: 6, md: 1 }}>
                    <Button fullWidth startIcon={<RestartAlt />} sx={{ textTransform: 'none', color: 'text.secondary' }}>Reset</Button>
                </Grid>
                <Grid size={{ xs: 6, md: 2 }} sx={{ textAlign: 'right' }}>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setOpen(true)}
                        sx={{ borderRadius: '8px', textTransform: 'none' }}
                    >
                        Create Appraisal
                    </Button>
                </Grid>
            </Grid>

            {/* Appraisal Table */}
            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow >
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Employee</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Period</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Status</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Score</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                            {filteredData.map((row) => (
                                <TableRow key={row.id} hover sx={{
                                    '&:hover': {
                                        bgcolor: '#F1F5F9 !important'
                                    }
                                }}>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: '#6366F1', fontSize: '14px' }}>{row.avatar}</Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="600">{row.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">{row.role}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}><Typography variant="body2">{row.period}</Typography></TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Chip
                                            label={row.status}
                                            size="small"
                                            sx={{
                                                bgcolor: getStatusColor(row.status).bg,
                                                color: getStatusColor(row.status).color,
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                border: `1px solid ${getStatusColor(row.status).border || '#cbd5e1'}`
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }}>
                                        <Typography variant="body2" fontWeight="700" color={row.score > 4 ? 'success.main' : 'text.primary'}>
                                            {row.score > 0 ? row.score : '--'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={{ color: '#1E293B' }} align="center">
                                        <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => navigate('/manager/view-appraisals')}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="View past appraisals">
                                            <IconButton size="small" onClick={() => navigate('/manager/past-appraisals')}>
                                                <History fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            {/* Create Dialog */}
            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ borderBottom: '1px solid #eee', mb: 1 }}>
                    Create New Appraisal
                    <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <CreateAppraisal onClose={() => setOpen(false)} />
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default EmployeeAppraisals;